import { Col, Form, Select } from "antd";
import { useEffect, useState, useCallback } from "react";
import { getData } from "../../../apiServices/common";
import {
  PACKING_LINES_ENDPOINT,
  PACKING_TABLE_ENDPOINT,
  GET_DIGITALIZATION_APP_USERS,
  SALES_CONTRACT_LIST,
  PO_MAP_NEW_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import CustomDatePicker from "../../../common/CustomDatePicker";

const { Option } = Select;

const HourlyPackingConfirmationFilterFields = ({ initialTableData }) => {
  const [packingLinesList, setPackingLinesList] = useState([]);
  const [packingTablesList, setPackingTablesList] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);
  const [salesContractList, setSalesContractList] = useState([]);
  const [poMapList, setPoMapList] = useState([]);
  const [hourList, setHourList] = useState([]);

  // Fetch packing lines
  const getPackingLines = useCallback(async (searchValue = null) => {
    const query = `${PACKING_LINES_ENDPOINT}`;
    const bodyData = {
      ...(searchValue && { name: searchValue }),
      page: 1,
      per_page: 100,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200 || response?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setPackingLinesList(data);
    }
  }, []);

  // Fetch packing tables
  const getPackingTables = useCallback(async (searchValue = null) => {
    const query = `${PACKING_TABLE_ENDPOINT}`;
    const bodyData = {
      ...(searchValue && { name: searchValue }),
      page: 1,
      per_page: 100,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200 || response?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setPackingTablesList(data);
    }
  }, []);

  // Fetch operators
  const getOperators = useCallback(async (searchValue = null) => {
    const query = `${GET_DIGITALIZATION_APP_USERS}`;
    const bodyData = {
      ...(searchValue && { name: searchValue }),
      page: 1,
      per_page: 100,
    };

    const response = await getData(query, false, bodyData);
    if (response?.data?.code === 200) {
      const data = response?.data?.data?.data || [];
      setOperatorsList(data);
    }
  }, []);

  // Fetch sales contracts
  const getSalesContracts = useCallback(async (searchValue = null) => {
    const query = `${SALES_CONTRACT_LIST}`;
    const bodyData = {
      ...(searchValue && { reference_no: searchValue }),
      page: 1,
      per_page: 100,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200 || response?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setSalesContractList(data);
    }
  }, []);

  // Fetch PO maps
  const getPoMaps = useCallback(async (searchValue = null) => {
    const query = `${PO_MAP_NEW_ENDPOINT}`;
    const bodyData = {
      ...(searchValue && { original_po: searchValue }),
      page: 1,
      per_page: 100,
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200 || response?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setPoMapList(data);
    }
  }, []);

  // Fetch hour configurations
  const getHourConfigurations = useCallback(async () => {
    const query = `/api/digitalization/hourly-production-records-hours-list`;
    try {
      const response = await getData(query);
      if (response?.data?.code === 200 || response?.status === 200) {
        const data = response?.data?.data || [];
        setHourList(data);
      }
    } catch (error) {
      console.error("Error fetching hour configurations:", error);
    }
  }, []);

  useEffect(() => {
    getPackingLines();
    getPackingTables();
    getOperators();
    getSalesContracts();
    getPoMaps();
    getHourConfigurations();
  }, [getPackingLines, getPackingTables, getOperators, getSalesContracts, getPoMaps, getHourConfigurations]);

  return (
    <>
      <Col span={4}>
        <Form.Item label="Entry Date" name="entry_date">
          <CustomDatePicker
            style={{ width: "100%" }}
            size="small"
            placeholder="Select Entry Date"
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Packing Line" name="packing_line_id">
          <Select
            showSearch
            allowClear
            placeholder="Select Packing Line"
            onSearch={(value) => getPackingLines(value)}
            onClear={() => getPackingLines()}
            optionFilterProp="children"
            filterOption={false}
            size="small"
          >
            {packingLinesList.map((line) => (
              <Option key={line.id} value={line.id}>
                {line.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Packing Table" name="packing_table_id">
          <Select
            showSearch
            allowClear
            placeholder="Select Packing Table"
            onSearch={(value) => getPackingTables(value)}
            onClear={() => getPackingTables()}
            optionFilterProp="children"
            filterOption={false}
            size="small"
          >
            {packingTablesList.map((table) => (
              <Option key={table.id} value={table.id}>
                {table.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Sales Contract" name="sales_contract_id">
          <Select
            showSearch
            allowClear
            placeholder="Select Sales Contract"
            onSearch={(value) => getSalesContracts(value)}
            onClear={() => getSalesContracts()}
            optionFilterProp="children"
            filterOption={false}
            size="small"
          >
            {salesContractList.map((contract) => (
              <Option key={contract.id} value={contract.id}>
                {contract.reference_no}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Operator" name="operator_id">
          <Select
            showSearch
            allowClear
            placeholder="Select Operator"
            onSearch={(value) => getOperators(value)}
            onClear={() => getOperators()}
            optionFilterProp="children"
            filterOption={false}
            size="small"
          >
            {operatorsList.map((operator) => (
              <Option key={operator.id} value={operator.id}>
                {operator.name} ({operator?.additional_info?.id_card_no || "-"})
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="PO Map" name="po_map_id">
          <Select
            showSearch
            allowClear
            placeholder="Select PO Map"
            onSearch={(value) => getPoMaps(value)}
            onClear={() => getPoMaps()}
            optionFilterProp="children"
            filterOption={false}
            size="small"
          >
            {poMapList.map((poMap) => (
              <Option key={poMap.id} value={poMap.id}>
                {poMap.original_po}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Hour" name="hour">
          <Select
            showSearch
            allowClear
            placeholder="Select Hour"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            size="small"
          >
            {hourList.map((hourConfig) => (
              <Option key={hourConfig.hour} value={hourConfig.hour}>
                {hourConfig.hour}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </>
  );
};

export default HourlyPackingConfirmationFilterFields;

